import { create } from "zustand";

export interface OrderModifyEvent {
  id: string; // unique identifier for this event
  timestamp: number; // when the event was received
  data: any; // raw data from server
  processed: boolean; // whether this event has been shown to user
}

interface OrderModifyStore {
  // State
  events: OrderModifyEvent[];
  lastEventId: string | null;

  // Actions
  addOrderModifyEvent: (data: any) => string;
  markEventAsProcessed: (eventId: string) => void;
  getUnprocessedEvents: () => OrderModifyEvent[];
  clearProcessedEvents: () => void;
  clearAllEvents: () => void;
}

export const useOrderModifyStore = create<OrderModifyStore>((set, get) => ({
  // Initial state
  events: [],
  lastEventId: null,

  // Add new order modify event
  addOrderModifyEvent: (data: any) => {
    const eventId = `order_modify_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newEvent: OrderModifyEvent = {
      id: eventId,
      timestamp: Date.now(),
      data: data,
      processed: false,
    };

    set((state) => ({
      events: [...state.events, newEvent],
      lastEventId: eventId,
    }));

    return eventId;
  },

  // Mark event as processed (user has seen the toast)
  markEventAsProcessed: (eventId: string) => {
    set((state) => ({
      events: state.events.map((event) =>
        event.id === eventId ? { ...event, processed: true } : event
      ),
    }));
  },

  // Get all unprocessed events (for showing toasts)
  getUnprocessedEvents: () => {
    return get().events.filter((event) => !event.processed);
  },

  // Clear all processed events (cleanup)
  clearProcessedEvents: () => {
    set((state) => ({
      events: state.events.filter((event) => !event.processed),
    }));
  },

  // Clear all events
  clearAllEvents: () => {
    set({
      events: [],
      lastEventId: null,
    });
  },
}));

// Selector hooks for convenience
export const useUnprocessedOrderModifyEvents = () =>
  useOrderModifyStore((state) => state.getUnprocessedEvents());

export const useLastOrderModifyEvent = () =>
  useOrderModifyStore((state) => 
    state.lastEventId 
      ? state.events.find(e => e.id === state.lastEventId) 
      : null
  );
